import { useState, useEffect, useCallback, useRef } from 'react';
import { QueryDocumentSnapshot, DocumentData } from 'firebase/firestore';
import { useDebouncedCallback } from 'use-debounce';
import { AffiliateWithStats } from '../types';
import { AffiliateSortField, AffiliateSortOrder } from '../components/AffiliateSort';
import { 
  fetchAffiliatesPaginated, 
  PaginatedAffiliatesResult,
  searchAffiliates,
  SearchAffiliatesResult
} from '../services/affiliateService';

interface UsePaginatedAffiliatesOptions {
  pageSize?: number;
  enabled?: boolean;
}

interface UsePaginatedAffiliatesResult {
  affiliates: AffiliateWithStats[];
  loading: boolean;
  error: string | null;
  totalCount: number;
  currentPage: number;
  totalPages: number;
  hasMore: boolean;
  searchQuery: string;
  selectedStatus: string;
  sortField: AffiliateSortField | null;
  sortOrder: AffiliateSortOrder;
  isSearchMode: boolean;
  isFilterMode: boolean;
  isSortMode: boolean;
  isCombinedMode: boolean;
  searchLoading: boolean;
  filterLoading: boolean;
  sortLoading: boolean;
  setSearchQuery: (query: string) => void;
  setSelectedStatus: (status: string) => void;
  setSorting: (field: AffiliateSortField | null, order: AffiliateSortOrder) => void;
  handleSearch: (query: string) => void;
  fetchNextPage: () => Promise<void>;
  fetchPage: (page: number) => Promise<void>;
  refresh: () => Promise<void>;
  reset: () => void;
  updateAffiliateInCache: (updatedAffiliate: AffiliateWithStats) => void;
  removeAffiliateFromCache: (affiliateId: string) => void;
  invalidateCache: () => void;
}

/**
 * Hook for paginated affiliate fetching with search and filtering
 * Matches the orders page architecture exactly
 */
export const usePaginatedAffiliates = ({ 
  pageSize = 20,
  enabled = true 
}: UsePaginatedAffiliatesOptions = {}): UsePaginatedAffiliatesResult => {
  // Server affiliates state (for browse mode)
  const [serverAffiliates, setServerAffiliates] = useState<AffiliateWithStats[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  
  // Search state management
  const [isSearchMode, setIsSearchMode] = useState(false);
  const [searchCache, setSearchCache] = useState<Map<string, SearchAffiliatesResult>>(new Map());
  const [searchResults, setSearchResults] = useState<AffiliateWithStats[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchCurrentPage, setSearchCurrentPage] = useState(1);
  
  // Filter state management
  const [isFilterMode, setIsFilterMode] = useState(false);
  const [filterCache, setFilterCache] = useState<Map<string, SearchAffiliatesResult>>(new Map());
  const [filterResults, setFilterResults] = useState<AffiliateWithStats[]>([]);
  const [filterLoading, setFilterLoading] = useState(false);
  const [filterCurrentPage, setFilterCurrentPage] = useState(1);
  
  // Sort state management
  const [isSortMode, setIsSortMode] = useState(false);
  const [sortCache, setSortCache] = useState<Map<string, SearchAffiliatesResult>>(new Map());
  const [sortResults, setSortResults] = useState<AffiliateWithStats[]>([]);
  const [sortLoading, setSortLoading] = useState(false);
  const [sortCurrentPage, setSortCurrentPage] = useState(1);
  
  // Combined mode state (when search, filters, and/or sorting are active)
  const [combinedResults, setCombinedResults] = useState<AffiliateWithStats[]>([]);
  const [combinedCurrentPage, setCombinedCurrentPage] = useState(1);
  
  // Computed state for combined mode
  const isCombinedMode = (isSearchMode && isFilterMode) || (isSearchMode && isSortMode) || (isFilterMode && isSortMode) || (isSearchMode && isFilterMode && isSortMode);
  
  // Filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('all');
  
  // Sort state
  const [sortField, setSortField] = useState<AffiliateSortField | null>(null);
  const [sortOrder, setSortOrder] = useState<AffiliateSortOrder>('desc');
  
  // Final filtered affiliates (what gets displayed)
  const [affiliates, setAffiliates] = useState<AffiliateWithStats[]>([]);
  
  // Use ref for pagination cursors to avoid dependency issues
  const pageCursorsRef = useRef<Map<number, QueryDocumentSnapshot<DocumentData> | null>>(new Map());
  
  const totalPages = Math.ceil(totalCount / pageSize);

  // Fetch all affiliates for search (with caching)
  const fetchAffiliatesForSearch = useCallback(async (): Promise<AffiliateWithStats[]> => {
    const cacheKey = 'search_all';
    
    // Check cache first
    const cached = searchCache.get(cacheKey);
    if (cached) {
      console.log('Using cached search data for affiliates');
      return cached.affiliates;
    }

    try {
      setSearchLoading(true);
      setError(null);
      
      const result = await searchAffiliates('');
      
      // Cache the result
      setSearchCache(prev => new Map(prev).set(cacheKey, result));
      
      return result.affiliates;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch affiliates for search';
      setError(errorMessage);
      console.error('Search fetch error:', error);
      return [];
    } finally {
      setSearchLoading(false);
    }
  }, [searchCache]);

  // Fetch all affiliates for filtering (with caching)
  const fetchAffiliatesForFilter = useCallback(async (): Promise<AffiliateWithStats[]> => {
    const cacheKey = 'filter_all';
    
    // Check cache first
    const cached = filterCache.get(cacheKey);
    if (cached) {
      console.log('Using cached filter data for affiliates');
      return cached.affiliates;
    }

    try {
      setFilterLoading(true);
      setError(null);
      
      const result = await searchAffiliates('');
      
      // Cache the result
      setFilterCache(prev => new Map(prev).set(cacheKey, result));
      
      return result.affiliates;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch affiliates for filtering';
      setError(errorMessage);
      console.error('Filter fetch error:', error);
      return [];
    } finally {
      setFilterLoading(false);
    }
  }, [filterCache]);

  // Perform client-side search
  const performSearch = useCallback((query: string, affiliates: AffiliateWithStats[]): AffiliateWithStats[] => {
    if (!query.trim()) return affiliates;
    
    const searchTerm = query.toLowerCase();
    return affiliates.filter(affiliate => {
      // Search by name, email, and referral code
      if (affiliate.userName.toLowerCase().includes(searchTerm) ||
          affiliate.userEmail.toLowerCase().includes(searchTerm)) {
        return true;
      }
      
      // Search by referral code
      if (affiliate.referralCode && affiliate.referralCode.toLowerCase().includes(searchTerm)) {
        return true;
      }
      
      return false;
    });
  }, []);

  // Perform client-side filtering
  const performFilter = useCallback((status: string, affiliates: AffiliateWithStats[]): AffiliateWithStats[] => {
    if (status === 'all') return affiliates;
    
    return affiliates.filter(affiliate => affiliate.status === status);
  }, []);

  // Perform client-side sorting
  const performSort = useCallback((field: AffiliateSortField, order: AffiliateSortOrder, affiliates: AffiliateWithStats[]): AffiliateWithStats[] => {
    const sortedAffiliates = [...affiliates].sort((a, b) => {
      let aValue: any;
      let bValue: any;
      
      switch (field) {
        case 'submittedAt':
          aValue = new Date(a.submittedAt).getTime();
          bValue = new Date(b.submittedAt).getTime();
          break;
        case 'reviewedAt':
          aValue = a.reviewedAt ? new Date(a.reviewedAt).getTime() : 0;
          bValue = b.reviewedAt ? new Date(b.reviewedAt).getTime() : 0;
          break;
        case 'userName':
          aValue = a.userName.toLowerCase();
          bValue = b.userName.toLowerCase();
          break;
        case 'userEmail':
          aValue = a.userEmail.toLowerCase();
          bValue = b.userEmail.toLowerCase();
          break;
        case 'status':
          aValue = a.status;
          bValue = b.status;
          break;
        case 'totalEarnings':
          aValue = a.stats.totalEarnings;
          bValue = b.stats.totalEarnings;
          break;
        case 'totalClicks':
          aValue = a.stats.totalClicks;
          bValue = b.stats.totalClicks;
          break;
        case 'totalConversions':
          aValue = a.stats.totalConversions;
          bValue = b.stats.totalConversions;
          break;
        default:
          return 0;
      }
      
      if (aValue < bValue) return order === 'asc' ? -1 : 1;
      if (aValue > bValue) return order === 'asc' ? 1 : -1;
      return 0;
    });
    
    return sortedAffiliates;
  }, []);

  // Paginate results
  const paginateResults = useCallback((affiliates: AffiliateWithStats[], page: number): AffiliateWithStats[] => {
    const startIndex = (page - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return affiliates.slice(startIndex, endIndex);
  }, [pageSize]);

  // Debounced search handler
  const debouncedSearch = useDebouncedCallback(async (query: string) => {
    if (!query.trim()) {
      setIsSearchMode(false);
      setSearchResults([]);
      setSearchCurrentPage(1);
      return;
    }

    setIsSearchMode(true);
    setSearchCurrentPage(1);
    
    try {
      const allAffiliates = await fetchAffiliatesForSearch();
      const searchResults = performSearch(query, allAffiliates);
      setSearchResults(searchResults);
      setTotalCount(searchResults.length);
    } catch (error) {
      console.error('Search error:', error);
    }
  }, 300);

  // Handle search input
  const handleSearch = useCallback((query: string) => {
    setSearchQuery(query);
    debouncedSearch(query);
  }, [debouncedSearch]);

  // Handle filter changes
  const handleFilterChange = useCallback(async (status: string) => {
    setSelectedStatus(status);
    
    if (status === 'all') {
      setIsFilterMode(false);
      setFilterResults([]);
      setFilterCurrentPage(1);
      return;
    }
    
    setIsFilterMode(true);
    setFilterCurrentPage(1);
    
    try {
      const allAffiliates = await fetchAffiliatesForFilter();
      const filterResults = performFilter(status, allAffiliates);
      setFilterResults(filterResults);
      setTotalCount(filterResults.length);
    } catch (error) {
      console.error('Filter error:', error);
    }
  }, [fetchAffiliatesForFilter, performFilter]);

  // Handle sort changes
  const handleSortChange = useCallback(async (field: AffiliateSortField | null, order: AffiliateSortOrder) => {
    setSortField(field);
    setSortOrder(order);
    
    if (!field) {
      setIsSortMode(false);
      setSortResults([]);
      setSortCurrentPage(1);
      return;
    }
    
    setIsSortMode(true);
    setSortCurrentPage(1);
    
    try {
      const allAffiliates = await fetchAffiliatesForSearch();
      const sortResults = performSort(field, order, allAffiliates);
      setSortResults(sortResults);
      setTotalCount(sortResults.length);
    } catch (error) {
      console.error('Sort error:', error);
    }
  }, [fetchAffiliatesForSearch, performSort]);

  // Handle combined mode
  const handleCombinedMode = useCallback(async () => {
    if (!isCombinedMode) return;
    
    setCombinedCurrentPage(1);
    
    try {
      let allAffiliates = await fetchAffiliatesForSearch();
      
      // Apply search if active
      if (isSearchMode && searchQuery.trim()) {
        allAffiliates = performSearch(searchQuery, allAffiliates);
      }
      
      // Apply filter if active
      if (isFilterMode && selectedStatus !== 'all') {
        allAffiliates = performFilter(selectedStatus, allAffiliates);
      }
      
      // Apply sort if active
      if (isSortMode && sortField) {
        allAffiliates = performSort(sortField, sortOrder, allAffiliates);
      }
      
      setCombinedResults(allAffiliates);
      setTotalCount(allAffiliates.length);
    } catch (error) {
      console.error('Combined mode error:', error);
    }
  }, [isCombinedMode, isSearchMode, searchQuery, isFilterMode, selectedStatus, isSortMode, sortField, sortOrder, fetchAffiliatesForSearch, performSearch, performFilter, performSort]);

  // Fetch server affiliates (browse mode)
  const fetchServerAffiliates = useCallback(async (page: number = 1) => {
    if (isSearchMode || isFilterMode || isSortMode) return;
    
    try {
      setLoading(true);
      setError(null);
      
      const cursor = pageCursorsRef.current.get(page);
      
      const result: PaginatedAffiliatesResult = await fetchAffiliatesPaginated(
        pageSize,
        cursor,
        undefined // No status filter for browse mode
      );
      
      setServerAffiliates(result.affiliates);
      setTotalCount(result.total || 0);
      setHasMore(result.hasMore);
      
      // Store cursor for next page
      if (result.lastDoc) {
        pageCursorsRef.current.set(page + 1, result.lastDoc);
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch affiliates';
      setError(errorMessage);
      console.error('Server fetch error:', error);
    } finally {
      setLoading(false);
    }
  }, [pageSize, isSearchMode, isFilterMode, isSortMode]);

  // Handle page changes
  const fetchPage = useCallback(async (page: number) => {
    setCurrentPage(page);
    
    if (isCombinedMode) {
      setCombinedCurrentPage(page);
    } else if (isSearchMode) {
      setSearchCurrentPage(page);
    } else if (isFilterMode) {
      setFilterCurrentPage(page);
    } else if (isSortMode) {
      setSortCurrentPage(page);
    } else {
      await fetchServerAffiliates(page);
    }
  }, [isCombinedMode, isSearchMode, isFilterMode, isSortMode, fetchServerAffiliates]);

  // Fetch next page
  const fetchNextPage = useCallback(async () => {
    if (currentPage < totalPages) {
      await fetchPage(currentPage + 1);
    }
  }, [currentPage, totalPages, fetchPage]);

  // Refresh data
  const refresh = useCallback(async () => {
    // Clear all caches
    setSearchCache(new Map());
    setFilterCache(new Map());
    setSortCache(new Map());
    pageCursorsRef.current.clear();
    
    // Reset to first page
    setCurrentPage(1);
    setSearchCurrentPage(1);
    setFilterCurrentPage(1);
    setSortCurrentPage(1);
    setCombinedCurrentPage(1);
    
    // Fetch fresh data based on current mode
    if (isCombinedMode) {
      await handleCombinedMode();
    } else if (isSearchMode) {
      await debouncedSearch(searchQuery);
    } else if (isFilterMode) {
      await handleFilterChange(selectedStatus);
    } else if (isSortMode) {
      await handleSortChange(sortField, sortOrder);
    } else {
      await fetchServerAffiliates(1);
    }
  }, [isCombinedMode, isSearchMode, isFilterMode, isSortMode, searchQuery, selectedStatus, sortField, sortOrder, handleCombinedMode, debouncedSearch, handleFilterChange, handleSortChange, fetchServerAffiliates]);

  // Reset to browse mode
  const reset = useCallback(() => {
    setSearchQuery('');
    setSelectedStatus('all');
    setSortField(null);
    setSortOrder('desc');
    setIsSearchMode(false);
    setIsFilterMode(false);
    setIsSortMode(false);
    setSearchResults([]);
    setFilterResults([]);
    setSortResults([]);
    setCombinedResults([]);
    setCurrentPage(1);
    setSearchCurrentPage(1);
    setFilterCurrentPage(1);
    setSortCurrentPage(1);
    setCombinedCurrentPage(1);
    
    // Clear caches
    setSearchCache(new Map());
    setFilterCache(new Map());
    setSortCache(new Map());
    pageCursorsRef.current.clear();
  }, []);

  // Cache management
  const updateAffiliateInCache = useCallback((updatedAffiliate: AffiliateWithStats) => {
    const updateAffiliateInArray = (affiliates: AffiliateWithStats[]) => 
      affiliates.map(affiliate => affiliate.id === updatedAffiliate.id ? updatedAffiliate : affiliate);
    
    setServerAffiliates(updateAffiliateInArray);
    setSearchResults(updateAffiliateInArray);
    setFilterResults(updateAffiliateInArray);
    setSortResults(updateAffiliateInArray);
    setCombinedResults(updateAffiliateInArray);
  }, []);

  const removeAffiliateFromCache = useCallback((affiliateId: string) => {
    const removeAffiliateFromArray = (affiliates: AffiliateWithStats[]) => 
      affiliates.filter(affiliate => affiliate.id !== affiliateId);
    
    setServerAffiliates(removeAffiliateFromArray);
    setSearchResults(removeAffiliateFromArray);
    setFilterResults(removeAffiliateFromArray);
    setSortResults(removeAffiliateFromArray);
    setCombinedResults(removeAffiliateFromArray);
    
    // Update total count
    setTotalCount(prev => Math.max(0, prev - 1));
  }, []);

  const invalidateCache = useCallback(() => {
    setSearchCache(new Map());
    setFilterCache(new Map());
    setSortCache(new Map());
    pageCursorsRef.current.clear();
  }, []);

  // Update final affiliates based on mode
  useEffect(() => {
    let currentResults: AffiliateWithStats[] = [];
    let currentPageNum: number = 1;
    
    if (isCombinedMode) {
      currentResults = combinedResults;
      currentPageNum = combinedCurrentPage;
    } else if (isSearchMode) {
      currentResults = searchResults;
      currentPageNum = searchCurrentPage;
    } else if (isFilterMode) {
      currentResults = filterResults;
      currentPageNum = filterCurrentPage;
    } else if (isSortMode) {
      currentResults = sortResults;
      currentPageNum = sortCurrentPage;
    } else {
      currentResults = serverAffiliates;
      currentPageNum = currentPage;
    }
    
    // Apply pagination if not in browse mode
    if (isSearchMode || isFilterMode || isSortMode || isCombinedMode) {
      currentResults = paginateResults(currentResults, currentPageNum);
    }
    
    setAffiliates(currentResults);
  }, [
    isCombinedMode, isSearchMode, isFilterMode, isSortMode,
    combinedResults, combinedCurrentPage,
    searchResults, searchCurrentPage,
    filterResults, filterCurrentPage,
    sortResults, sortCurrentPage,
    serverAffiliates, currentPage,
    paginateResults
  ]);

  // Initial data fetch
  useEffect(() => {
    if (enabled) {
      fetchServerAffiliates(1);
    }
  }, [enabled, fetchServerAffiliates]);

  // Handle combined mode updates
  useEffect(() => {
    if (isCombinedMode) {
      handleCombinedMode();
    }
  }, [isCombinedMode, handleCombinedMode]);

  // Handle filter changes
  useEffect(() => {
    if (selectedStatus !== 'all') {
      handleFilterChange(selectedStatus);
    }
  }, [selectedStatus, handleFilterChange]);

  // Handle sort changes
  useEffect(() => {
    if (sortField) {
      handleSortChange(sortField, sortOrder);
    }
  }, [sortField, sortOrder, handleSortChange]);

  return {
    affiliates,
    loading,
    error,
    totalCount,
    currentPage,
    totalPages,
    hasMore,
    searchQuery,
    selectedStatus,
    sortField,
    sortOrder,
    isSearchMode,
    isFilterMode,
    isSortMode,
    isCombinedMode,
    searchLoading,
    filterLoading,
    sortLoading,
    setSearchQuery,
    setSelectedStatus,
    setSorting: handleSortChange,
    handleSearch,
    fetchNextPage,
    fetchPage,
    refresh,
    reset,
    updateAffiliateInCache,
    removeAffiliateFromCache,
    invalidateCache,
  };
}; 